import type React from 'react';
import { useState } from 'react';
import type { Table, VisibilityState, ColumnOrderState } from '@tanstack/react-table';
import { X, SlidersHorizontal, GripVertical, ListOrdered, Search, Trash2, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  title?: string;
  subtitle?: string;
  activeFilters: Record<string, string>;
  onClearFilters: () => void;
  onColumnOrderChange: (order: ColumnOrderState) => void;
  selectedCount?: number;
  onDeleteSelected?: () => void;
  onEditSelected?: () => void;
}

export function DataTableToolbar<TData>({
  table,
  title,
  subtitle,
  activeFilters,
  onClearFilters,
  onColumnOrderChange,
  selectedCount,
  onDeleteSelected,
  onEditSelected
}: DataTableToolbarProps<TData>) {
  const hasFilters = Object.keys(activeFilters).length > 0;

  // Column visibility state
  const [visOpen, setVisOpen] = useState(false);
  const [visSearch, setVisSearch] = useState('');
  const [draftVisibility, setDraftVisibility] = useState<VisibilityState>({});

  // Column order state
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [draftOrder, setDraftOrder] = useState<string[]>([]);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const allColumns = table.getAllColumns().filter(column => column.getCanHide());

  // ---- Column Visibility handlers ----
  const handleVisOpen = (isOpen: boolean) => {
    if (isOpen) {
      const current: VisibilityState = {};
      for (const col of allColumns) {
        current[col.id] = col.getIsVisible();
      }
      setDraftVisibility(current);
      setVisSearch('');
    }
    setVisOpen(isOpen);
  };

  const handleVisToggle = (columnId: string, checked: boolean) => {
    setDraftVisibility(prev => ({ ...prev, [columnId]: checked }));
  };

  const handleVisApply = () => {
    for (const col of allColumns) {
      const shouldBeVisible = draftVisibility[col.id] ?? true;
      if (col.getIsVisible() !== shouldBeVisible) {
        col.toggleVisibility(shouldBeVisible);
      }
    }
    setVisOpen(false);
  };

  const handleVisReset = () => {
    const reset: VisibilityState = {};
    for (const col of allColumns) {
      reset[col.id] = true;
    }
    setDraftVisibility(reset);
  };

  const handleVisCancel = () => {
    setVisOpen(false);
  };

  const filteredVisColumns = allColumns.filter(column => {
    if (!visSearch) {
      return true;
    }
    const label = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;
    return label.toLowerCase().includes(visSearch.toLowerCase());
  });

  // ---- Column Order handlers ----
  const handleOrderOpen = (isOpen: boolean) => {
    if (isOpen) {
      setDraftOrder(
        table
          .getVisibleLeafColumns()
          .filter(c => c.id !== '_select')
          .map(c => c.id)
      );
      setOrderSearch('');
      setDraggedItem(null);
    }
    setOrderOpen(isOpen);
  };

  const handleOrderDragStart = (columnId: string) => {
    setDraggedItem(columnId);
  };

  const handleOrderDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) {
      return;
    }

    setDraftOrder(prev => {
      const dragIndex = prev.indexOf(draggedItem);
      const targetIndex = prev.indexOf(targetId);
      if (dragIndex === -1 || targetIndex === -1) {
        return prev;
      }

      const newOrder = [...prev];
      newOrder.splice(dragIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);
      return newOrder;
    });
  };

  const handleOrderDragEnd = () => {
    setDraggedItem(null);
  };

  const handleOrderApply = () => {
    const allIds = table.getAllLeafColumns().map(c => c.id);
    const visibleSet = new Set([...draftOrder, '_select']);
    const hiddenColumns = allIds.filter(id => !visibleSet.has(id));
    onColumnOrderChange(['_select', ...draftOrder, ...hiddenColumns]);
    setOrderOpen(false);
  };

  const handleOrderReset = () => {
    const visibleSet = new Set(table.getVisibleLeafColumns().map(c => c.id));
    setDraftOrder(
      table
        .getAllLeafColumns()
        .map(c => c.id)
        .filter(id => visibleSet.has(id) && id !== '_select')
    );
  };

  const handleOrderCancel = () => {
    setOrderOpen(false);
  };

  const getColumnLabel = (columnId: string) => {
    const col = table.getColumn(columnId);
    if (!col) {
      return columnId;
    }
    return typeof col.columnDef.header === 'string' ? col.columnDef.header : columnId;
  };

  const filteredOrderColumns = draftOrder.filter(columnId => {
    if (!orderSearch) {
      return true;
    }
    return getColumnLabel(columnId).toLowerCase().includes(orderSearch.toLowerCase());
  });

  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="flex items-center gap-3">
        {title && (
          <div>
            <h1 className="text-2xl font-bold tracking-tight leading-tight">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
        )}
        {hasFilters && (
          <Button variant="ghost" onClick={onClearFilters} className="h-8 px-2 lg:px-3">
            Clear filters
            <X className="ml-2 size-4" />
          </Button>
        )}
        {selectedCount != null && selectedCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedCount} selected</span>
            {selectedCount === 1 && onEditSelected && (
              <Button variant="outline" size="sm" className="h-8" onClick={onEditSelected}>
                <Pencil className="mr-2 size-4" />
                Edit
              </Button>
            )}
            {onDeleteSelected && (
              <Button variant="destructive" size="sm" className="h-8" onClick={onDeleteSelected}>
                <Trash2 className="mr-2 size-4" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Column Order Popover */}
        <Popover open={orderOpen} onOpenChange={handleOrderOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8">
              <ListOrdered className="mr-2 size-4" />
              Column Order
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[280px] p-0">
            <div className="flex items-center justify-between px-4 py-3">
              <h4 className="text-sm font-semibold">Reorder Columns</h4>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleOrderReset}
              >
                Reset
              </Button>
            </div>
            <div className="px-4 pb-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search columns..."
                  value={orderSearch}
                  onChange={e => setOrderSearch(e.target.value)}
                  className="h-8 pl-7 text-xs"
                />
              </div>
            </div>
            <Separator />
            <div className="max-h-[300px] overflow-y-auto px-2 py-2 space-y-0.5">
              {filteredOrderColumns.map(columnId => (
                <div
                  key={columnId}
                  draggable={!orderSearch}
                  onDragStart={() => handleOrderDragStart(columnId)}
                  onDragOver={e => handleOrderDragOver(e, columnId)}
                  onDragEnd={handleOrderDragEnd}
                  className={`flex items-center gap-2 rounded-md px-2 py-2 text-sm select-none
                    ${draggedItem === columnId ? 'opacity-50 bg-accent' : 'hover:bg-accent'}
                    ${!orderSearch ? 'cursor-grab active:cursor-grabbing' : ''}
                  `}
                >
                  <GripVertical className="size-4 text-muted-foreground/50 shrink-0" />
                  <span className="truncate">{getColumnLabel(columnId)}</span>
                </div>
              ))}
              {filteredOrderColumns.length === 0 && (
                <div className="py-2 text-xs text-muted-foreground text-center">No columns found</div>
              )}
            </div>
            <Separator />
            <div className="flex items-center justify-end gap-2 px-4 py-3">
              <Button variant="outline" size="sm" onClick={handleOrderCancel}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleOrderApply}>
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Column Visibility Popover */}
        <Popover open={visOpen} onOpenChange={handleVisOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8">
              <SlidersHorizontal className="mr-2 size-4" />
              Columns
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[260px] p-0">
            <div className="flex items-center justify-between px-4 py-3">
              <h4 className="text-sm font-semibold">Column Visibility</h4>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleVisReset}
              >
                Reset
              </Button>
            </div>
            <div className="px-4 pb-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search columns..."
                  value={visSearch}
                  onChange={e => setVisSearch(e.target.value)}
                  className="h-8 pl-7 text-xs"
                />
              </div>
            </div>
            <Separator />
            <div className="max-h-[300px] overflow-y-auto px-4 py-2 space-y-1">
              {filteredVisColumns.map(column => {
                const label = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;
                const isChecked = draftVisibility[column.id] ?? true;
                return (
                  <label
                    key={column.id}
                    className="flex items-center gap-2 rounded-sm px-1 py-1.5 text-sm hover:bg-accent"
                  >
                    <Checkbox checked={isChecked} onCheckedChange={checked => handleVisToggle(column.id, !!checked)} />
                    {label}
                  </label>
                );
              })}
              {filteredVisColumns.length === 0 && (
                <div className="py-2 text-xs text-muted-foreground text-center">No columns found</div>
              )}
            </div>
            <Separator />
            <div className="flex items-center justify-end gap-2 px-4 py-3">
              <Button variant="outline" size="sm" onClick={handleVisCancel}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleVisApply}>
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
